/**
 * A tabela do Dashboard: cinco colunas, todas ordenáveis.
 *
 * Papel: reproduz `#dashTable` (l. 557–566) e as linhas de `renderDashLike`
 * (l. 933–946) — ÁREA, ITEM, ANDAMENTO com a barra, ATUALIZAÇÃO centralizada e
 * RESPONSÁVEL.
 *
 * Como usar: a aba passa as linhas já filtradas, na ordem da API; a ordenação
 * vive aqui, e sobrevive à troca de aba porque o `QuadroEmbutido` mantém a aba
 * montada. ANDAMENTO ordena pelo percentual. Não há contador de registros: o da
 * origem escreve num `#dashCount` que não existe (l. 948–949). Esta é a tabela
 * que vai para o papel em "Exportar PDF", com o desenho de impressão do kit.
 */
import { type Corretiva } from '@concierge/shared';

import { CabecalhoOrdenavel, type ColunaOrdenavel, useOrdenacao } from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import { formatarDataDaTabela, situacaoDaLinhaVazia } from '../regras';
import { PERCENTUAL_DO_ANDAMENTO } from '../rotulos';
import { type SituacaoDaLista } from '../types';
import { BarraDeAndamento } from './kit/barra-de-andamento';
import {
  CLASSE_DO_CABECALHO_ORDENAVEL,
  Celula,
  LinhaDaTabela,
  LinhaVazia,
  Tabela,
} from './kit/tabela';

/** Como cada coluna do Dashboard ordena. */
const COLUNAS: Readonly<Record<string, ColunaOrdenavel<Corretiva>>> = {
  area: { tipo: 'texto', valor: (corretiva) => corretiva.area },
  descricao: { tipo: 'texto', valor: (corretiva) => corretiva.descricao },
  andamento: {
    tipo: 'numero',
    valor: (corretiva) => PERCENTUAL_DO_ANDAMENTO[corretiva.status],
  },
  atualizacao: { tipo: 'data', valor: (corretiva) => corretiva.atualizacao },
  responsavel: { tipo: 'texto', valor: (corretiva) => corretiva.responsavel },
};

/** Os cabeçalhos, na ordem e com a largura da origem. */
const CABECALHOS = [
  { chave: 'area', rotulo: 'ÁREA', largura: 'w-[13%]' },
  { chave: 'descricao', rotulo: 'ITEM', largura: 'w-[35%]' },
  { chave: 'andamento', rotulo: 'ANDAMENTO', largura: 'w-[20%]' },
  { chave: 'atualizacao', rotulo: 'ATUALIZAÇÃO', largura: 'w-[16%]' },
  { chave: 'responsavel', rotulo: 'RESPONSÁVEL', largura: 'w-[16%]' },
] as const;

interface TabelaDashboardProps {
  /** As corretivas que passaram nos filtros, na ordem da API. */
  linhas: readonly Corretiva[];
  situacao: SituacaoDaLista;
  /** Chamado pelo "Tentar de novo" da linha de falha. */
  onTentarDeNovo: () => void;
}

/**
 * A tabela do Dashboard.
 *
 * @param linhas - As corretivas filtradas.
 * @param situacao - A situação da consulta.
 * @param onTentarDeNovo - Relê a lista depois de uma falha.
 * @returns A tabela.
 */
export function TabelaDashboard({ linhas, situacao, onTentarDeNovo }: TabelaDashboardProps) {
  const ordenacao = useOrdenacao(linhas, COLUNAS);
  const vazia = situacaoDaLinhaVazia(situacao, linhas.length);

  return (
    <Tabela>
      <thead>
        <tr>
          {CABECALHOS.map(({ chave, rotulo, largura }) => (
            <CabecalhoOrdenavel
              key={chave}
              seta={ordenacao.seta(chave)}
              onAlternar={() => ordenacao.alternar(chave)}
              className={cn(CLASSE_DO_CABECALHO_ORDENAVEL, largura)}
            >
              {rotulo}
            </CabecalhoOrdenavel>
          ))}
        </tr>
      </thead>
      <tbody>
        {vazia !== null ? (
          <LinhaVazia colunas={5} situacao={vazia} onTentarDeNovo={onTentarDeNovo} />
        ) : (
          ordenacao.linhas.map((corretiva) => (
            <LinhaDaTabela key={corretiva.id}>
              <Celula tipo="titulo">{corretiva.area}</Celula>
              <Celula tipo="quebra">{corretiva.descricao}</Celula>
              <Celula>
                <BarraDeAndamento percentual={PERCENTUAL_DO_ANDAMENTO[corretiva.status]} />
              </Celula>
              <Celula tipo="fraco" className="text-center">
                {formatarDataDaTabela(corretiva.atualizacao)}
              </Celula>
              <Celula tipo="fraco">{corretiva.responsavel}</Celula>
            </LinhaDaTabela>
          ))
        )}
      </tbody>
    </Tabela>
  );
}
